import React, {
  useState, useEffect, useLayoutEffect, useRef, useCallback,
} from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { ChromePicker } from 'react-color';
import cn from 'classnames';

import Opener from '../../components/Opener';
import { outy } from '../../shared/utils';
import Input from '../input'; // eslint-disable-line
import './styles.scss';

/**
 * ColorPicker
 * @param {Object} props
 * @param {String} props.initialColor
 * @param {Function} props.onColorChange - live preview while dragging (optional)
 * @param {Function} props.onColorChangeComplete
 * @param {Boolean} props.disabled
 * @param {Boolean} props.commitOnPickerClose - commit on opener close instead of each drag mouse-up
 * @param {Boolean} props.swatchOnly
 * @param {String} props.openerText
 * @param {Function} props.onOpenChange
 * @returns {JSX}
 */
export default function ColorPicker({
  initialColor,
  onColorChange,
  onColorChangeComplete,
  disabled,
  error,
  commitOnPickerClose = false,
  swatchOnly = false,
  openerText = 'Open colorPicker',
  onOpenChange = null,
}) {
  const [color, setColor] = useState(initialColor || '#fff');
  const [isColorPickerOpen, setColorPickerOpen] = useState(false);
  const [portalPosition, setPortalPosition] = useState(null);
  const wasPickerOpenRef = useRef(false);
  const rootRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (commitOnPickerClose && isColorPickerOpen) return;
    if (initialColor !== color) {
      setColor(initialColor);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialColor, isColorPickerOpen, commitOnPickerClose]);

  useEffect(() => {
    if (wasPickerOpenRef.current && !isColorPickerOpen && commitOnPickerClose) {
      onColorChangeComplete(color);
    }
    wasPickerOpenRef.current = isColorPickerOpen;
  }, [isColorPickerOpen, color, commitOnPickerClose, onColorChangeComplete]);

  const setOpen = useCallback((isOpen) => {
    setColorPickerOpen(isOpen);
    if (onOpenChange) onOpenChange(isOpen);
  }, [onOpenChange]);

  const updatePortalPosition = useCallback(() => {
    const rect = rootRef.current?.getBoundingClientRect();
    if (!rect) return;

    setPortalPosition({
      top: rect.bottom + 5,
      left: rect.left,
    });
  }, []);

  useLayoutEffect(() => {
    if (!swatchOnly || !isColorPickerOpen) {
      setPortalPosition(null);
      return undefined;
    }

    updatePortalPosition();
    window.addEventListener('resize', updatePortalPosition);
    window.addEventListener('scroll', updatePortalPosition, true);

    return () => {
      window.removeEventListener('resize', updatePortalPosition);
      window.removeEventListener('scroll', updatePortalPosition, true);
    };
  }, [isColorPickerOpen, swatchOnly, updatePortalPosition]);

  useEffect(() => {
    if (!swatchOnly || !isColorPickerOpen) return undefined;

    const nodes = [rootRef.current, dropdownRef.current].filter(Boolean);
    const handleOutsideClick = outy(nodes, ['click'], () => setOpen(false));

    return () => handleOutsideClick.remove();
  }, [isColorPickerOpen, setOpen, swatchOnly, portalPosition]);

  const handleColorChange = useCallback((nextColor) => {
    const { hex } = nextColor;
    setColor(hex);
    if (onColorChange) onColorChange(hex);
  }, [onColorChange]);

  const handlePickerChangeComplete = useCallback((nextColor) => {
    setColor(nextColor.hex);
    if (!commitOnPickerClose) {
      onColorChangeComplete(nextColor.hex);
    }
  }, [commitOnPickerClose, onColorChangeComplete]);

  const handleColorBlur = useCallback(() => {
    onColorChangeComplete(color);
  }, [color, onColorChangeComplete]);

  const handlePickerKeyDown = (event) => {
    if (event.key !== 'Enter') return;

    event.preventDefault();
    // With commitOnPickerClose the close effect already commits once — avoid a second call.
    if (!commitOnPickerClose) {
      onColorChangeComplete(color);
    }
    setOpen(false);
  };

  const handleSwatchToggle = () => {
    if (disabled) return;
    setOpen(!isColorPickerOpen);
  };

  if (swatchOnly) {
    return (
      <div
        ref={rootRef}
        className={cn('colorPicker', {
          isColorPickerOpen,
          isDisabled: disabled,
          isSwatchOnly: true,
        })}
      >
        <div className="colorPickerValue" style={{ backgroundColor: color }} />
        <button
          type="button"
          className="openerLink"
          aria-label={openerText}
          disabled={disabled}
          onClick={handleSwatchToggle}
        >
          {openerText}
        </button>
        <If condition={isColorPickerOpen && portalPosition}>
          {createPortal(
            <div
              ref={dropdownRef}
              className="colorPickerDropdownPortal"
              style={{ top: portalPosition.top, left: portalPosition.left }}
              onKeyDown={handlePickerKeyDown}
            >
              <ChromePicker
                color={color}
                onChange={handleColorChange}
                onChangeComplete={handlePickerChangeComplete}
                disableAlpha
              />
            </div>,
            document.body,
          )}
        </If>
      </div>
    );
  }

  return (
    <div className={cn('colorPicker', { isColorPickerOpen, isDisabled: disabled })}>
      <Input
        value={color}
        error={error}
        onChange={(e, value) => handleColorChange({ hex: value })}
        onBlur={handleColorBlur}
      />
      <div className="colorPickerValue" style={{ backgroundColor: color }} />
      <Opener
        hideOnClickOutside
        openerText={openerText}
        hideOpenerWhenOpen={false}
        parentHandler={setOpen}
        additionalClassName="openerLink"
      >
        <span onKeyDown={handlePickerKeyDown}>
          <ChromePicker
            color={color}
            disableAlpha
            onChange={handleColorChange}
            onChangeComplete={handlePickerChangeComplete}
          />
        </span>
      </Opener>
    </div>
  );
}

ColorPicker.propTypes = {
  initialColor: PropTypes.string,
  disabled: PropTypes.bool,
  error: PropTypes.string,
  commitOnPickerClose: PropTypes.bool,
  swatchOnly: PropTypes.bool,
  openerText: PropTypes.string,
  onOpenChange: PropTypes.func,
  onColorChange: PropTypes.func,
  onColorChangeComplete: PropTypes.func.isRequired,
};

ColorPicker.defaultProps = {
  initialColor: '#fff',
  disabled: false,
  error: null,
  onColorChange: null,
  onOpenChange: null,
  commitOnPickerClose: false,
  swatchOnly: false,
  openerText: 'Open colorPicker',
};
